import React from 'react';
import '../styles/HomePage.css';

function HomePage({ onNavigate }) {
  // 处理卡片点击事件
  const handleCardClick = (submenu, parentKey) => {
    if (onNavigate) {
      onNavigate(submenu, parentKey);
    }
  };
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Welcome to MATAI</h1>
          <h2 className="hero-subtitle">All-in-One Solution for AI-Driven Materials Design</h2>
          <p className="hero-description">
            MATAI is a pioneering research framework and interdisciplinary team dedicated to transforming the way materials are discovered, designed, and developed. We integrate artificial intelligence with deep materials science expertise—spanning theory, simulation, and experimentation—to overcome the complexity and inefficiency of traditional materials R&D.
          </p>
        </div>
      </section>

      {/* MATAI Core Section */}
      <section className="section">
        <div className="section-content">
          <h2 className="section-title">MATAI Core: Foundation for Materials Discovery</h2>
          <p className="section-intro">
            Our core toolkit provides essential AI-driven tools and platforms that support every stage of materials development:
          </p>
          
          <div className="features-grid">
            <div className="feature-item clickable" onClick={() => handleCardClick('Database', 'base')}>
              <div className="feature-icon">🗄️</div>
              <h3 className="feature-title">Holistic Database</h3>
              <ul className="feature-description">
                <li>Curated, structured, and expandable database</li>
                <li>Captures full complexity of material composition</li>
                <li>Includes structure, processing, and properties</li>
              </ul>
            </div>
            
            <div className="feature-item clickable" onClick={() => handleCardClick('Predictor', 'base')}>
              <div className="feature-icon">🧠</div>
              <h3 className="feature-title">Foundational Predictor</h3>
              <ul className="feature-description">
                <li>AI engine for robust property predictions</li>
                <li>Works across different material types</li>
                <li>Handles limited or noisy data effectively</li>
              </ul>
            </div>
            
            <div className="feature-item clickable" onClick={() => handleCardClick('Designer', 'base')}>
              <div className="feature-icon">⚡</div>
              <h3 className="feature-title">Generalist Designer</h3>
              <ul className="feature-description">
                <li>Versatile design tool for new materials</li>
                <li>Tailored to multiple property targets</li>
                <li>Handles even unseen property combinations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MATAI Tools Section */}
      <section className="section tools-overview-section">
        <div className="section-content">
          <h2 className="section-title">MATAI Tools: Applied AI Utilities</h2>
          <p className="section-intro">
            Practical, ready-to-use calculators and copilots that help researchers move faster from raw measurements to shareable results.
          </p>

          <div className="features-grid tools-feature-grid">
            <div
              className="feature-item clickable"
              onClick={() => handleCardClick('Offset Yield Strength', 'tools')}
            >
              <div className="feature-icon tools-feature-icon">📈</div>
              <h3 className="feature-title">Experiment-Ready Calculators</h3>
              <ul className="feature-description">
                <li>Detect 0.2% offset yield from uploaded stress–strain curves</li>
                <li>Compute densities and mass/atomic conversions with built-in data</li>
                <li>Generate clean outputs for lab notes, reports, and simulations</li>
              </ul>
            </div>

            <div
              className="feature-item clickable"
              onClick={() => handleCardClick('Data Extraction', 'tools')}
            >
              <div className="feature-icon tools-feature-icon">🤖</div>
              <h3 className="feature-title">Automated Data Extraction</h3>
              <ul className="feature-description">
                <li>Turn PDFs into structured datasets with summaries and logs</li>
                <li>Capture tables, figures, and text for downstream modeling</li>
                <li>Export clean CSVs to accelerate analysis pipelines</li>
              </ul>
            </div>

            <div
              className="feature-item clickable"
              onClick={() => handleCardClick('Matai Chat', 'tools')}
            >
              <div className="feature-icon tools-feature-icon">💬</div>
              <h3 className="feature-title">Collaborative Copilot</h3>
              <ul className="feature-description">
                <li>Ask materials questions and trigger tools directly via chat</li>
                <li>Link predictions, explanations, and design suggestions in one place</li>
                <li>Stay aligned with MATAI Core data and models</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MATAI Pro Section - temporarily hidden. Re-enable this block to restore the Pro section cards. */}
      {/**
      <section className="section pro-section">
        <div className="section-content">
          <h2 className="section-title">MATAI Pro: Intelligence for the Next Frontier</h2>
          <p className="section-intro">
            For power users and collaborators, our MATAI Pro Suite offers advanced autonomous capabilities:
          </p>
          
          <div className="pro-features">
            <div className="pro-feature clickable" onClick={() => handleCardClick('Auto Data', 'pro')}>
              <div className="pro-feature-header">
                <div className="pro-feature-icon">🔄</div>
                <h3>Automatic Data Pipeline</h3>
              </div>
              <ul className="pro-feature-description">
                <li>Continuously mines knowledge from literature</li>
                <li>Structures data using large language models</li>
                <li>Automated database expansion</li>
              </ul>
            </div>
            
            <div className="pro-feature clickable" onClick={() => handleCardClick('Auto Simulation', 'pro')}>
              <div className="pro-feature-header">
                <div className="pro-feature-icon">🔬</div>
                <h3>Simulation-in-the-Loop</h3>
              </div>
              <ul className="pro-feature-description">
                <li>Integrates DFT and other simulations</li>
                <li>Automatically verifies predictions</li>
                <li>Refines material property models</li>
              </ul>
            </div>
            
            <div className="pro-feature clickable" onClick={() => handleCardClick('Chat', 'pro')}>
              <div className="pro-feature-header">
                <div className="pro-feature-icon">💬</div>
                <h3>MATAI Chat</h3>
              </div>
              <ul className="pro-feature-description">
                <li>Interactive materials science assistant</li>
                <li>Trained on specialized knowledge</li>
                <li>Enables intuitive exploration and discovery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      **/}

      {/* Data Pipeline Section - hidden */}
      {/**
      <section className="section data-pipeline-section">
        <div className="section-content">
          <h2 className="section-title">Data Processing Pipeline</h2>
          <p className="section-intro">
            Our comprehensive data processing pipeline transforms raw materials data into high-quality training datasets through systematic filtering and validation:
          </p>

          <div className="data-funnel">
            
          </div>
        </div>
      </section>
      **/}

      {/* Architecture Section - hidden */}
      {/**
      <section className="section architecture-section">
        <div className="section-content">
          <h2 className="section-title">System Architecture</h2>
          <p className="section-intro">
            MATAI's comprehensive architecture integrates foundational AI tools with advanced autonomous capabilities:
          </p>

          <div className="architecture-diagram">
            <div className="architecture-layer matai-pro">
              <div className="layer-header">
                <h3>MATAI Pro</h3>
                <span className="layer-subtitle">Advanced Autonomous Capabilities</span>
              </div>
              <div className="layer-modules">
                <div className="module pro-module">
                  <div className="module-icon">🔄</div>
                  <div className="module-name">Auto Data Pipeline</div>
                  <div className="module-desc">Literature mining & structuring</div>
                </div>
                <div className="module pro-module">
                  <div className="module-icon">🔬</div>
                  <div className="module-name">Simulation-in-the-Loop</div>
                  <div className="module-desc">DFT integration & verification</div>
                </div>
                <div className="module pro-module">
                  <div className="module-icon">💬</div>
                  <div className="module-name">MATAI Chat</div>
                  <div className="module-desc">Intelligent assistant</div>
                </div>
              </div>
            </div>

            <div className="architecture-connector">
              <div className="connector-line"></div>
              <div className="connector-text">Built on</div>
            </div>

            <div className="architecture-layer matai-base">
              <div className="layer-header">
                <h3>MATAI Core</h3>
                <span className="layer-subtitle">Foundation for Materials Discovery</span>
              </div>
              <div className="layer-modules">
                <div className="module base-module">
                  <div className="module-icon">🗄️</div>
                  <div className="module-name">Holistic Database</div>
                  <div className="module-desc">Curated & structured data</div>
                </div>
                <div className="module base-module">
                  <div className="module-icon">🧠</div>
                  <div className="module-name">Foundational Predictor</div>
                  <div className="module-desc">AI-driven property prediction</div>
                </div>
                <div className="module base-module">
                  <div className="module-icon">⚡</div>
                  <div className="module-name">Generalist Designer</div>
                  <div className="module-desc">Multi-target optimization</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      **/}

      {/* Vision & Mission Section */}
      <section className="section vision-mission-section">
        <div className="section-content">
          <div className="vision-mission-grid">
            <div className="vision-block">
              <h2 className="block-title">Our Vision</h2>
              <p className="block-content">
                To lead a new era of materials discovery by building autonomous, intelligent systems that accelerate innovation from fundamental research to real-world application.
              </p>
            </div>
            
            <div className="mission-block">
              <h2 className="block-title">Our Mission</h2>
              <div className="mission-list">
                <div className="mission-item">
                  <span className="mission-bullet">•</span>
                  <span>Bridge AI and materials science through interdisciplinary collaboration</span>
                </div>
                <div className="mission-item">
                  <span className="mission-bullet">•</span>
                  <span>Create open, intelligent platforms that democratize advanced materials design</span>
                </div>
                <div className="mission-item">
                  <span className="mission-bullet">•</span>
                  <span>Build tools that are data-efficient, generalizable, and explainable</span>
                </div>
                <div className="mission-item">
                  <span className="mission-bullet">•</span>
                  <span>Enable sustainable, low-cost, and high-speed development of next-generation materials</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="section roadmap-section-main">
        <div className="section-content">
          <h2 className="section-title">Development Roadmap</h2>
          <p className="section-intro">
            Follow our journey as we expand the capabilities of MATAI to accelerate materials discovery across multiple domains:
          </p>
          
          {/* Timeline Graphic - hidden */}
          {/**
          <div className="timeline-graphic">
            <div className="timeline-header">
              <h3>Materials Research Evolution Timeline</h3>
            </div>
            <div className="timeline-visual">
              <div className="timeline-track"></div>
              <div className="timeline-phases">
                <div className="timeline-phase active">
                  <div className="phase-icon">🔗</div>
                  <div className="phase-label">HEA</div>
                  <div className="phase-period">2025 H1</div>
                </div>
                <div className="timeline-phase upcoming">
                  <div className="phase-icon">🧩</div>
                  <div className="phase-label">Composite</div>
                  <div className="phase-period">2025 H2</div>
                </div>
                <div className="timeline-phase future">
                  <div className="phase-icon">📄</div>
                  <div className="phase-label">2D Materials</div>
                  <div className="phase-period">2026 H1</div>
                </div>
                <div className="timeline-phase future">
                  <div className="phase-icon">🔬</div>
                  <div className="phase-label">Nanoparticles</div>
                  <div className="phase-period">2026 H2</div>
                </div>
                <div className="timeline-phase future">
                  <div className="phase-icon">⚡</div>
                  <div className="phase-label">Advanced</div>
                  <div className="phase-period">2027+</div>
                </div>
              </div>
            </div>
          </div>
          **/}
          
          <div className="horizontal-roadmap">
            <div className="roadmap-line"></div>
            
            <div className="roadmap-milestone completed">
              <div className="milestone-marker completed"></div>
              <div className="milestone-content">
                <div className="milestone-date">2nd Half of 2025</div>
                <div className="milestone-title">Alloy Design & Property Prediction Platform</div>
                <div className="milestone-description">Launch of our core platform for high-entropy alloys (HEAs), featuring AI-driven design tools and accurate, data-efficient property prediction engines.</div>
              </div>
            </div>
            
            <div className="roadmap-milestone upcoming">
              <div className="milestone-marker upcoming"></div>
              <div className="milestone-content">
                <div className="milestone-date">1st Half of 2026</div>
                <div className="milestone-title">Composite Materials</div>
                <div className="milestone-description">Expansion into multifunctional composites with intelligent tools optimized for capturing complex composition–structure–function relationships and multi-phase interactions.</div>
              </div>
            </div>
            
            <div className="roadmap-milestone future">
              <div className="milestone-marker future"></div>
              <div className="milestone-content">
                <div className="milestone-date">2nd Half of 2026</div>
                <div className="milestone-title">2D Materials</div>
                <div className="milestone-description">Deployment of AI-guided solutions for 2D materials, enabling automated structure generation, property forecasting, and synthesis parameter recommendation.</div>
              </div>
            </div>
            
            <div className="roadmap-milestone future">
              <div className="milestone-marker future"></div>
              <div className="milestone-content">
                <div className="milestone-date">1st Half of 2027</div>
                <div className="milestone-title">Nanoparticles</div>
                <div className="milestone-description">Development of a dedicated module for nanoparticle design, including phase-controlled synthesis pathways and predictive models for catalytic performance.</div>
              </div>
            </div>
            
            <div className="roadmap-milestone future">
              <div className="milestone-marker future"></div>
              <div className="milestone-content">
                <div className="milestone-date">2nd Half of 2027+</div>
                <div className="milestone-title">Advanced Materials</div>
                <div className="milestone-description">Continuous expansion into ceramics, polymers, quantum dots, and other emerging material classes to build a comprehensive materials discovery ecosystem.</div>
              </div>
            </div>
          </div>
          
        </div>
      </section>

      {/* Footer Section */}
      <section className="footer-section">
        <div className="section-content">
          <div className="footer-divider"></div>
          <div className="footer-links">
            <a href="https://www.ntu.edu.sg" target="_blank" rel="noopener noreferrer">NTU Singapore</a>
            <a href="https://www.ntu.edu.sg/computing" target="_blank" rel="noopener noreferrer">College of Computing and Data Science</a>
            <a href="https://www.ntu.edu.sg/mse" target="_blank" rel="noopener noreferrer">School of Materials Science & Engineering</a>
          </div>
          <div className="copyright">© {new Date().getFullYear()} MATAI Project. All Rights Reserved.</div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
